<script setup lang="ts">
import { computed, ref } from 'vue';
import { type DropdownOption } from 'naive-ui';
import { IconReorder } from '@/components/design/icons';
import type { VaultDirectoryInDbSlim } from '@/api';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDropdown, { gaiaCheckedIcon } from '@/components/design/base/GaiaDropdown.vue';
import GaiaWorkspacePane from '@/components/design/review/GaiaWorkspacePane.vue';
import FileVault from '@/components/file_vault/FileVault.vue';
import FileVaultUploadModal from '@/components/file_vault/FileVaultUploadModal.vue';
import type { FileVaultColumnKey } from '@/components/file_vault/fileVaultColumns';
import type { FileVaultActionKey } from '@/components/file_vault/fileVaultActions';
import ReviewProjectFilesList, {
  type ReviewFilesActionKey,
  type ReviewFilesMetaKey,
} from '@/components/reviewProject/ReviewProjectFilesList.vue';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';

/**
 * The data-room rail of the review workspace (Review Projects mock). The three
 * views the tabs used to hold — the usable files, the ones that failed to
 * import, and the raw vault tree — now sit behind the switcher in the pane's
 * header, because the rail is too narrow to carry a tab strip beside them.
 */
const props = defineProps<{
  rootDirectory: VaultDirectoryInDbSlim;
  projectUid: string;
  collapsed: boolean;
}>();

const emit = defineEmits<{ 'update:collapsed': [collapsed: boolean] }>();

const reviewProjectStore = useReviewProjectStore();

const VIEWS = [
  { key: 'files', label: 'Data Room' },
  { key: 'bad-files', label: 'Bad files' },
  { key: 'raw-data', label: 'Raw data' },
] as const;

const activeView = computed(() => reviewProjectStore.dataRoomTab);
const activeLabel = computed(
  () => VIEWS.find(view => view.key === activeView.value)?.label ?? VIEWS[0].label
);

const viewOptions = computed<DropdownOption[]>(() =>
  VIEWS.map(view => ({
    key: view.key,
    label: view.label,
    icon: gaiaCheckedIcon(view.key === activeView.value),
  }))
);

function selectView(key: string): void {
  reviewProjectStore.dataRoomTab = key;
}

// The pane's collapsed rail shows how much is in the view it is hiding.
const fileCount = ref<number | null>(null);
const countLabel = computed(() =>
  activeView.value === 'raw-data' || fileCount.value === null ? undefined : String(fileCount.value)
);

/* ------------------------------------------------------------ add files */

const showUploadModal = ref(false);

function handleUploaded(): void {
  // The list endpoint syncs the data room first, so a refetch picks the new
  // files up; the vault view reloads itself.
  reviewProjectStore.dataRoomFilesRefreshTick += 1;
}

/* ------------------------------------------------------------ view config */

const dataRoomColumns: FileVaultColumnKey[] = ['name', 'actions'];
// With inline-action-count 0, all of these fold into the "⋯" menu.
const dataRoomActions: FileVaultActionKey[] = ['rename', 'preview', 'download'];

const usableFilesColumns: ReviewFilesMetaKey[] = [
  'name',
  'version',
  'path',
  'tags',
  'seen',
  'reviewed',
  'actions',
];
const usableFilesActions: ReviewFilesActionKey[] = ['preview', 'notes'];
const unusableFilesColumns: ReviewFilesMetaKey[] = ['name', 'version', 'path', 'tags'];
</script>

<template>
  <gaia-workspace-pane
    :title="activeLabel"
    collapsible
    :collapsed="props.collapsed"
    :count="countLabel"
    divider="right"
    :scroll-body="false"
    data-tour="rp.data-room"
    @update:collapsed="value => emit('update:collapsed', value)"
  >
    <template #title>
      <gaia-dropdown :options="viewOptions" placement="bottom-start" @select="selectView">
        <gaia-button quaternary size="small" title="Switch view">
          <template #icon>
            <n-icon :component="IconReorder" />
          </template>
          {{ activeLabel }}
        </gaia-button>
      </gaia-dropdown>
    </template>

    <template #actions>
      <gaia-add-button subject="files" size="xs" @click="showUploadModal = true" />
    </template>

    <!-- Bad files stays non-draggable: unusable files can't run through a playbook. -->
    <review-project-files-list
      v-if="activeView === 'files'"
      :project-uid="props.projectUid"
      :usable="true"
      :columns="usableFilesColumns"
      :actions="usableFilesActions"
      draggable-rows
      enable-auto-tag
      @update:count="value => (fileCount = value)"
    />

    <review-project-files-list
      v-else-if="activeView === 'bad-files'"
      :project-uid="props.projectUid"
      :usable="false"
      :columns="unusableFilesColumns"
      @update:count="value => (fileCount = value)"
    />

    <!-- FileVault reads initial-* once per mount; it remounts on view switch, so
         live bindings restore the latest store state. -->
    <file-vault
      v-else
      :root-directory-uid="props.rootDirectory.uid"
      :root-label="props.rootDirectory.directory_name"
      :show-search="false"
      top-button-icon-mode
      :preview-on-dblclick="false"
      :actions="dataRoomActions"
      :inline-action-count="0"
      :columns="dataRoomColumns"
      :frozen-left="0"
      :frozen-right="0"
      :initial-directory-uid="reviewProjectStore.dataRoomDirectoryUid ?? undefined"
      :initial-selected-revision-uids="reviewProjectStore.dataRoomSelectedRevisionUids"
      @selection-change="reviewProjectStore.dataRoomSelectedRevisionUids = $event"
      @directory-change="reviewProjectStore.dataRoomDirectoryUid = $event"
    />
  </gaia-workspace-pane>

  <!-- The mock's "From repository" tab is left out: linking a repository file
       into a data room at its current version has no endpoint yet — the only
       vault operation available is a move, which would take the file out of the
       folder it belongs to. Uploading is the vault page's own modal. -->
  <file-vault-upload-modal
    v-model:show="showUploadModal"
    :parent-directory-uid="props.rootDirectory.uid"
    :parent-directory-name="props.rootDirectory.directory_name"
    @uploaded="handleUploaded"
  />
</template>
